<template>
  <BaseSelect
    :options="formattedOptions"
    :model-value="modelValue"
    :placeholder="placeholder"
    :searchable="true"
    :clearable="clearable"
    :with-button="withButton"
    :button-label="buttonLabel"
    :no-results-text="noResultsText"
    :external-search="true"
    :invalid="invalid"
    @update:model-value="handleUpdate"
    @change="handleChange"
    @button-click="$emit('button-click')"
    @search="handleSearch"
    @clear-search="handleClearSearch"
    @dropdown-scroll-end="handleScrollEnd"
  />
</template>

<script>
import { mapState } from "pinia";

import { usePaymentSourceStore } from "@/stores/paymentSourceStore";

export default {
  name: "PaymentSourceSelect",

  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Выберите источник оплаты",
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    withButton: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: "Создать источник оплаты",
    },
    noResultsText: {
      type: String,
      default: "Источники оплаты не найдены",
    },
  },

  emits: ["update:modelValue", "change", "button-click"],

  data() {
    return {
      preselectedOption: null,
      isSearchActive: false,
    };
  },

  computed: {
    ...mapState(usePaymentSourceStore, {
      selectItems: "selectItems",
      selectPagination: "selectPagination",
      selectIsLoading: "selectIsLoading",
    }),

    formattedOptions() {
      const options = this.selectItems.map((s) => ({
        value: s.id,
        label: s.selector_name,
      }));

      if (
        this.preselectedOption &&
        !this.isSearchActive &&
        !options.find(
          (o) =>
            String(o.value) === String(this.preselectedOption.value)
        )
      ) {
        return [this.preselectedOption, ...options];
      }

      return options;
    },
  },

  watch: {
    async modelValue(newVal, oldVal) {
      if (!newVal) {
        this.preselectedOption = null;
        return;
      }

      if (newVal === oldVal) return;

      if (!this.isSearchActive && !this.preselectedOption) {
        await this.fetchPreselectedOption();
      }
    },
  },

  methods: {
    handleUpdate(value) {
      if (!value) {
        this.preselectedOption = null;
      }

      this.$emit("update:modelValue", value);
    },

    handleChange(option) {
      if (option) {
        this.preselectedOption = {
          value: option.value,
          label: option.label,
        };
      } else {
        this.preselectedOption = null;
      }

      this.$emit("change", option ? option.value : null);
    },

    async handleSearch(searchData) {
      this.isSearchActive = true;
      const store = usePaymentSourceStore();
      await store.fetchSelectItems({ search: searchData.query });
    },

    async handleClearSearch() {
      this.isSearchActive = false;
      const store = usePaymentSourceStore();
      await store.fetchSelectItems();
    },

    async handleScrollEnd() {
      const store = usePaymentSourceStore();
      if (
        store.selectPagination.page < store.selectPagination.pages &&
        !store.selectIsLoading
      ) {
        await store.loadMoreSelectItems();
      }
    },

    async fetchPreselectedOption() {
      if (!this.modelValue) return;

      const existsInOptions = this.formattedOptions.find(
        (o) => String(o.value) === String(this.modelValue)
      );
      if (existsInOptions) {
        this.preselectedOption = null;
        return;
      }

      const store = usePaymentSourceStore();

      const existsInSelect = store.selectItems.find(
        (s) => String(s.id) === String(this.modelValue)
      );
      if (existsInSelect) {
        this.preselectedOption = {
          value: existsInSelect.id,
          label: existsInSelect.selector_name,
        };
        return;
      }

      try {
        const source = await store.showSource(this.modelValue);
        if (source && source.id) {
          this.preselectedOption = {
            value: source.id,
            label: source.selector_name,
          };
        }
      } catch (error) {
        console.error(
          "Ошибка при загрузке выбранного источника оплаты:",
          error
        );
      }
    },
  },

  async mounted() {
    const store = usePaymentSourceStore();
    await store.fetchSelectItems();
    await this.fetchPreselectedOption();
  },
};
</script>
